import { useMemo } from "react";

import { Column } from "@bridgesplit/ui";
import { AppButton } from "app/components/common";
import { useVaultEditRewardsTransaction } from "@bridgesplit/abf-react";
import { useTransactionSender } from "app/components/transactions";
import { convertDateJsToUnix, LOADING_ERROR, Result, uiAmountToLamports } from "@bridgesplit/utils";
import { UpdateRewardsScheduleTxnParams } from "@bridgesplit/abf-sdk";

import { ManagerEditRewardsProps } from "./ManagerEditRewardsDialog";
import { useManagerViewContext } from "../../ManagerViewContext";

export function ManagerEditRewardsCta(props: ManagerEditRewardsProps) {
    const { form, schedule } = props;
    const editRewards = useEditRewards(props);

    const ctaError = useMemo(() => {
        if (!schedule) return "Loading";
        if (!form.rewardToken) return "No reward token selected";
        if (!form.rewardAmount) return "No reward amount selected";
        return undefined;
    }, [form, schedule]);

    return (
        <Column spacing={1}>
            <AppButton
                asyncCta={{ onClickWithResult: editRewards }}
                isTransaction
                color={"secondary"}
                variant="contained"
                disabled={!!ctaError}
                sx={{ minWidth: "110px" }}
            >
                {ctaError ? ctaError : "Edit Rewards"}
            </AppButton>
        </Column>
    );
}

function useEditRewards({ form, schedule }: ManagerEditRewardsProps) {
    const editRewards = useVaultEditRewardsTransaction();
    const send = useTransactionSender();
    const { reset } = useManagerViewContext();

    return async () => {
        if (!schedule || !form.rewardToken || !form.rewardAmount || !form.rewardEndTime) {
            return Result.errFromMessage(LOADING_ERROR);
        }

        const rewardEndTime = convertDateJsToUnix(form.rewardEndTime);
        const amountToDeposit = uiAmountToLamports(form.rewardAmount, form.rewardToken.decimals);

        const isRewardEndTimeChanged = schedule.rewardEndTime !== rewardEndTime;

        const params: UpdateRewardsScheduleTxnParams = {
            vaultAddress: schedule.vaultAddress,
            scheduleIndex: schedule.scheduleIndex,
            amountToDeposit: amountToDeposit,
            newScheduleEndTime: isRewardEndTimeChanged ? rewardEndTime : null,
            rewardMint: schedule.rewardMetadata.mint //Currently no support for updating reward mint
        };

        return await send(editRewards, params, { sendOptions: { onSuccess: reset } });
    };
}
